import { ActivityIndicator, Pressable, ScrollView } from "react-native";
import { useLocalSearchParams } from "expo-router";
import { Text, View } from "@src/components";
import { sizes } from "@src/constants/sizes";
// @ts-expect-error baseline 2026-02-16
import { ReportsSection, ReportsListItem, ReportsListItemText } from "@src/components/web";
import { isWeb } from "@src/utils/web-helpers";
import { useWasteSenseReportList } from "@src/features/wastesense/hooks/useWasteSenseReportList";
import type { ReportListItem } from "@visioni/a3s-reports/client-messages";

/**
 * WasteSense report picker.
 *
 * Lists the tenant's completed WasteSense reports newest-first via
 * `useWasteSenseReportList` (the live `/reports/wastesense/list` endpoint,
 * filtered to `status: "ready"`). Each row is rendered as a real `<a href>`
 * on web so cmd/middle-click → new tab keeps working. Older reports beyond
 * the first page are reachable via an explicit "Load older reports" action;
 * the endpoint is cursor-paginated so adding more pages can't desync if the
 * generator stamps a new row at the front mid-scroll.
 */
export default function WasteSenseListScreen() {
  const { tenantId } = useLocalSearchParams<{ tenantId: string }>();
  const { reports, isLoading, isError, error, isTenantPending, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useWasteSenseReportList();

  if (!isWeb) {
    return (
      <View flex={1} alignItems="center" justifyContent="center" padding="3xl" gap="md">
        <Text textStyle="sm/semibold" color="text" textAlign="center">
          WasteSense reports are only available on the web client.
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          Open this page in your browser to view your reports.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{
        paddingTop: 40,
        paddingBottom: 40,
        paddingHorizontal: 80,
        maxWidth: sizes.webContentMaxWidth,
      }}
      showsVerticalScrollIndicator={false}
    >
      <View gap="6xl">
        <View gap="sm" px="md">
          <Text textStyle="lg/semibold" color="text">
            WasteSense
          </Text>
          <Text textStyle="sm/regular" color="textMuted">
            A digest of illegal dumping and site activity.
          </Text>
          <Text textStyle="sm/regular" color="textMuted">
            Provides a clear answer to: &ldquo;Did anything happen on our site?&rdquo;
          </Text>
        </View>

        <ReportsSection title="Weekly reports">
          <ReportListBody
            tenantId={tenantId}
            reports={reports}
            isLoading={isLoading || isTenantPending}
            isError={isError}
            error={error}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
          />
        </ReportsSection>
      </View>
    </ScrollView>
  );
}

interface ReportListBodyProps {
  tenantId: string;
  reports: readonly ReportListItem[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}

/**
 * Renders the live "Weekly reports" section body. Three terminal states
 * (loading, error, empty) are surfaced inline rather than collapsing the
 * section so the chrome stays stable across refetches. A "Load older
 * reports" pressable appears at the foot of the list whenever the endpoint
 * returns a cursor.
 */
function ReportListBody({
  tenantId,
  reports,
  isLoading,
  isError,
  error,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: ReportListBodyProps) {
  if (isLoading && reports.length === 0) {
    return (
      <View padding="3xl" alignItems="center" gap="md">
        <ActivityIndicator />
        <Text textStyle="xs/regular" color="textMuted">
          Loading reports…
        </Text>
      </View>
    );
  }

  if (isError) {
    return (
      <View padding="3xl" alignItems="center" gap="sm">
        <Text textStyle="sm/semibold" color="text">
          Couldn&apos;t load reports
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          {error?.message ?? "An unexpected error occurred."}
        </Text>
      </View>
    );
  }

  if (reports.length === 0) {
    return (
      <View padding="3xl" alignItems="center" gap="sm">
        <Text textStyle="sm/semibold" color="text">
          No reports yet
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          Reports appear here once the first weekly digest has been generated.
        </Text>
      </View>
    );
  }

  return (
    <View gap="md">
      {reports.map((row) => (
        <ReportsListItem
          key={row.reportId}
          title={formatPeriodTitle(row.periodStart, row.periodEnd)}
          href={`/tenants/${tenantId}/reports/wastesense/report?periodStart=${encodeURIComponent(row.periodStart)}`}
        >
          <ReportsListItemText>{describeRow(row)}</ReportsListItemText>
        </ReportsListItem>
      ))}
      {hasNextPage ?
        <Pressable
          onPress={isFetchingNextPage ? undefined : onLoadMore}
          accessibilityRole="button"
          accessibilityState={{ disabled: isFetchingNextPage }}
          style={{ paddingVertical: 12, alignItems: "center" }}
        >
          {isFetchingNextPage ?
            <View flexDirection="row" gap="sm" alignItems="center">
              <ActivityIndicator />
              <Text textStyle="xs/regular" color="textMuted">
                Loading older reports…
              </Text>
            </View>
          : <Text textStyle="xs/semibold" color="text">
              Load older reports
            </Text>
          }
        </Pressable>
      : null}
    </View>
  );
}

// `timeZone: "UTC"` pins the formatters to the same zone `parseIsoCalendarDate`
// stamps the date in. Without it `Intl.DateTimeFormat` formats UTC midnight
// in the viewer's local zone, so a viewer behind UTC (Americas, Europe in
// winter) sees a `2026-06-01` row labelled "Sun 31 May".
const WEEKDAY_FMT = new Intl.DateTimeFormat("en-AU", { weekday: "short", timeZone: "UTC" });
const DAY_MONTH_FMT = new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", timeZone: "UTC" });
const DAY_MONTH_YEAR_FMT = new Intl.DateTimeFormat("en-AU", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});
// `generatedAt` is a wall-clock instant (ISO date-time), not a calendar date —
// the viewer's local zone is the right one for this label, so it has no
// `timeZone` override.
const GENERATED_AT_FMT = new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", year: "numeric" });

/**
 * Formats a row's period bounds as e.g. "Mon 26 May – Sun 1 Jun 2026".
 * The year shows on the closing date only; repeating it on both sides is
 * noise unless the period straddles a year boundary.
 *
 * `periodStart` and `periodEnd` are calendar dates (`YYYY-MM-DD`) — the
 * `Date` constructor parses them as UTC midnight, which matches how the
 * generator stamps them. We use `Date` purely for `Intl.DateTimeFormat`'s
 * weekday lookup, not for any timezone-sensitive arithmetic.
 */
function formatPeriodTitle(periodStart: string, periodEnd: string): string {
  const start = parseIsoCalendarDate(periodStart);
  const end = parseIsoCalendarDate(periodEnd);
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const startLabel = sameYear ? DAY_MONTH_FMT.format(start) : DAY_MONTH_YEAR_FMT.format(start);
  return `${WEEKDAY_FMT.format(start)} ${startLabel} – ${WEEKDAY_FMT.format(end)} ${DAY_MONTH_YEAR_FMT.format(end)}`;
}

/**
 * `YYYY-MM-DD` → `Date` at UTC midnight. Avoids the parser's local-tz
 * interpretation that would shift "2026-06-01" by ±1 day on the wrong side
 * of an offset boundary.
 */
function parseIsoCalendarDate(s: string): Date {
  const [y, m, d] = s.split("-").map((p) => Number(p));
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
}

/**
 * Sub-label for a row. `generatedAt` is only present once `status === "ready"`
 * (which is what we filter to), but the type marks it optional, so we still
 * guard. When absent, the row's title already carries the period (so the
 * parent isn't ambiguous) and we surface a short status word — the parent
 * already filtered to ready rows, so `"Ready"` reflects reality without
 * fabricating a generation timestamp.
 */
function describeRow(row: ReportListItem): string {
  if (row.generatedAt) {
    return `Generated ${GENERATED_AT_FMT.format(new Date(row.generatedAt))}`;
  }
  return "Ready";
}
